import { useState, useEffect, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ComposableMap, Geographies, Geography, ZoomableGroup } from "react-simple-maps";
import { ArrowRight, X, Globe, Search, Calendar, DollarSign } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const TABS = [
  { key: "location", label: "By Location", icon: Globe },
  { key: "species", label: "By Species", icon: Search },
  { key: "season", label: "By Season", icon: Calendar },
  { key: "budget", label: "By Budget", icon: DollarSign },
];

const CATEGORY_ORDER = ["Big Game", "Dangerous Game", "Mountain Game", "Plains Game", "Waterfowl", "Wingshooting", "Fishing", "Predators"];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const BUDGET_RANGES = [
  { label: "Under $5,000", min: 0, max: 5000 },
  { label: "$5,000 – $15,000", min: 5000, max: 15000 },
  { label: "$15,000 – $30,000", min: 15000, max: 30000 },
  { label: "$30,000 – $75,000", min: 30000, max: 75000 },
  { label: "$75,000+", min: 75000, max: 200000 },
];

/* ISO 3166-1 numeric → alpha-2 mapping (subset for our countries) */
const NUM_TO_ALPHA = {
  "840": "US", "124": "CA", "484": "MX", "032": "AR", "152": "CL", "076": "BR",
  "170": "CO", "858": "UY", "188": "CR", "320": "GT", "591": "PA", "084": "BZ",
  "558": "NI", "710": "ZA", "834": "TZ", "716": "ZW", "508": "MZ", "894": "ZM",
  "516": "NA", "072": "BW", "231": "ET", "120": "CM", "140": "CF", "854": "BF",
  "417": "KG", "762": "TJ", "398": "KZ", "496": "MN", "586": "PK", "524": "NP",
  "156": "CN", "643": "RU", "036": "AU", "554": "NZ", "724": "ES", "826": "GB",
  "276": "DE", "250": "FR", "040": "AT", "756": "CH", "380": "IT", "348": "HU",
  "616": "PL", "642": "RO", "191": "HR", "688": "RS", "100": "BG", "705": "SI",
  "203": "CZ", "703": "SK", "752": "SE", "578": "NO", "246": "FI", "352": "IS",
  "372": "IE", "792": "TR", "196": "CY", "044": "BS", "192": "CU", "690": "SC",
  "462": "MV", "132": "CV", "404": "KE", "504": "MA", "788": "TN", "540": "NC",
  "004": "AF", "860": "UZ",
};

export default function BrowseHunts() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "location";
  const initialCountry = searchParams.get("country") || null;

  const [allSpecies, setAllSpecies] = useState([]);
  const [countries, setCountries] = useState({});
  const [selectedCountry, setSelectedCountry] = useState(initialCountry);
  const [countrySpecies, setCountrySpecies] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedBudget, setSelectedBudget] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/global-species`),
      axios.get(`${API}/api/countries`),
    ]).then(([specRes, countRes]) => {
      setAllSpecies(specRes.data);
      setCountries(countRes.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (selectedCountry) {
      axios.get(`${API}/api/countries/${selectedCountry}`).then((r) => {
        setCountrySpecies(r.data.species || []);
      }).catch(() => setCountrySpecies([]));
    }
  }, [selectedCountry]);

  useEffect(() => {
    if (initialCountry && initialCountry !== selectedCountry) {
      setSelectedCountry(initialCountry);
    }
  }, [initialCountry, selectedCountry]);

  const setTab = (tab) => {
    setSearchParams({ tab });
    setSelectedCountry(null);
    setCountrySpecies([]);
    setSearchTerm("");
    setSelectedCategory(null);
    setSelectedMonth(null);
    setSelectedBudget(null);
  };

  const countrySet = useMemo(() => new Set(Object.keys(countries)), [countries]);

  const getCountryColor = (geoId) => {
    const alpha2 = NUM_TO_ALPHA[geoId];
    if (!alpha2) return "#3B3B3B";
    if (alpha2 === selectedCountry) return "#B8860B";
    if (countrySet.has(alpha2)) return "#5C4033";
    return "#3B3B3B";
  };

  const handleCountryClick = (geo) => {
    const alpha2 = NUM_TO_ALPHA[geo.id];
    if (alpha2 && countrySet.has(alpha2)) {
      setSelectedCountry(alpha2);
      setSearchParams({ tab: "location", country: alpha2 });
    }
  };

  /* Species filter for the Species tab */
  const filteredSpecies = useMemo(() => {
    let result = allSpecies;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      result = result.filter((s) => s.name.toLowerCase().includes(q) || s.category.toLowerCase().includes(q) || (s.description || "").toLowerCase().includes(q));
    }
    if (selectedCategory) {
      result = result.filter((s) => s.category === selectedCategory);
    }
    return result;
  }, [allSpecies, searchTerm, selectedCategory]);

  /* Season filter */
  const seasonSpecies = useMemo(() => {
    if (!selectedMonth) return allSpecies;
    return allSpecies.filter((s) => s.season && s.season.includes(selectedMonth));
  }, [allSpecies, selectedMonth]);

  /* Budget filter */
  const budgetSpecies = useMemo(() => {
    if (!selectedBudget) return allSpecies;
    return allSpecies.filter((s) => s.price_low <= selectedBudget.max && s.price_high >= selectedBudget.min);
  }, [allSpecies, selectedBudget]);

  const groupByCategory = (list) => {
    const grouped = {};
    list.forEach((s) => {
      if (!grouped[s.category]) grouped[s.category] = [];
      grouped[s.category].push(s);
    });
    return CATEGORY_ORDER.filter((c) => grouped[c]).map((c) => ({ category: c, species: grouped[c] }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center pt-20">
        <p className="font-body text-slate">Loading species database...</p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Expeditions | Dispatch Hunts</title>
        <meta name="description" content="Browse hunting and fishing expeditions worldwide. Search by location, species, season, or budget." />
      </Helmet>

      {/* Header */}
      <section className="bg-carbon grain pt-28 pb-8">
        <div className="container-dh">
          <p className="text-label text-brass mb-3">Expedition Catalog</p>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-bone mb-2" data-testid="browse-headline">Real hunts. Real availability.</h1>
          <p className="font-body text-bone/50 mb-8">Browse {allSpecies.length}+ species across {Object.keys(countries).length}+ countries. Starting prices listed.</p>

          {/* Tabs */}
          <div className="flex gap-2 overflow-x-auto hide-scrollbar" data-testid="browse-tabs">
            {TABS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={`flex items-center gap-2 px-5 py-3 font-label text-xs font-semibold tracking-wider uppercase whitespace-nowrap transition-colors ${activeTab === key ? "bg-brass text-carbon" : "bg-carbon border border-bone/20 text-bone/70 hover:text-bone hover:border-bone/40"}`}
                data-testid={`browse-tab-${key}`}
              >
                <Icon className="w-4 h-4" /> {label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Tab Content */}
      <section className="bg-bone min-h-[60vh]">
        {/* ─── LOCATION TAB ─── */}
        {activeTab === "location" && (
          <div className="container-dh py-10">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Map */}
              <div className="lg:col-span-2 bg-carbon/95 border border-carbon/20 overflow-hidden" data-testid="world-map">
                <ComposableMap
                  projection="geoMercator"
                  projectionConfig={{ scale: 130, center: [20, 20] }}
                  style={{ width: "100%", height: "auto" }}
                >
                  <ZoomableGroup>
                    <Geographies geography={GEO_URL}>
                      {({ geographies }) =>
                        geographies.map((geo) => (
                          <Geography
                            key={geo.rsmKey}
                            geography={geo}
                            fill={getCountryColor(geo.id)}
                            stroke="#1A1A1A"
                            strokeWidth={0.5}
                            style={{
                              default: { outline: "none", cursor: NUM_TO_ALPHA[geo.id] && countrySet.has(NUM_TO_ALPHA[geo.id]) ? "pointer" : "default" },
                              hover: { fill: NUM_TO_ALPHA[geo.id] && countrySet.has(NUM_TO_ALPHA[geo.id]) ? "#B8860B" : "#3B3B3B", outline: "none" },
                              pressed: { outline: "none" },
                            }}
                            onClick={() => handleCountryClick(geo)}
                          />
                        ))
                      }
                    </Geographies>
                  </ZoomableGroup>
                </ComposableMap>
                <div className="px-4 pb-3 flex items-center gap-4">
                  <div className="flex items-center gap-2"><div className="w-3 h-3 bg-[#5C4033]" /><span className="text-bone/50 text-xs font-label">Hunts Available</span></div>
                  <div className="flex items-center gap-2"><div className="w-3 h-3 bg-brass" /><span className="text-bone/50 text-xs font-label">Selected</span></div>
                </div>
              </div>

              {/* Country Panel */}
              <div className="bg-chalk border border-carbon/15 p-6" data-testid="country-panel">
                {selectedCountry && countries[selectedCountry] ? (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-display text-2xl font-bold text-carbon">{countries[selectedCountry].name}</h3>
                      <button onClick={() => { setSelectedCountry(null); setCountrySpecies([]); setSearchParams({ tab: "location" }); }} className="text-slate hover:text-carbon"><X className="w-5 h-5" /></button>
                    </div>
                    <p className="font-label text-xs font-semibold tracking-[0.2em] uppercase text-brass mb-1">{countries[selectedCountry].continent}</p>
                    <p className="font-body text-sm text-slate mb-4">{countrySpecies.length} huntable species</p>
                    <Link to={`/destinations/${selectedCountry}`} className="inline-flex items-center gap-1 font-label text-xs text-brass hover:underline mb-4" data-testid="view-destination-detail">
                      View full destination page <ArrowRight className="w-3 h-3" />
                    </Link>
                    <div className="space-y-3 max-h-[400px] overflow-y-auto">
                      {countrySpecies.map((s) => (
                        <Link
                          key={s.slug}
                          to={`/expeditions/${s.slug}`}
                          className="block bg-bone border border-carbon/10 p-4 hover:border-brass/30 hover:shadow-lg transition-all"
                          data-testid={`country-species-${s.slug}`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <h4 className="font-display text-base font-bold text-carbon">{s.name}</h4>
                            <span className="font-label text-[10px] tracking-wider uppercase text-brass">{s.category}</span>
                          </div>
                          <p className="font-body text-xs text-slate mb-2">{s.season}</p>
                          <p className="font-mono text-sm text-carbon">
                            ${s.price_low.toLocaleString()} – ${s.price_high.toLocaleString()}
                          </p>
                        </Link>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12">
                    <Globe className="w-12 h-12 text-brass/40 mx-auto mb-4" />
                    <h3 className="font-display text-xl font-bold text-carbon mb-2">Select a Country</h3>
                    <p className="font-body text-sm text-slate">Click any highlighted country on the map to see available hunts and species.</p>
                    {/* Quick country list */}
                    <div className="mt-6 grid grid-cols-2 gap-2 max-h-[300px] overflow-y-auto">
                      {Object.entries(countries).sort((a, b) => a[1].name.localeCompare(b[1].name)).map(([code, c]) => (
                        <button
                          key={code}
                          onClick={() => { setSelectedCountry(code); setSearchParams({ tab: "location", country: code }); }}
                          className="text-left px-3 py-2 bg-bone border border-carbon/10 hover:border-brass/30 text-sm font-body text-carbon transition-colors"
                          data-testid={`country-btn-${code}`}
                        >
                          {c.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ─── SPECIES TAB ─── */}
        {activeTab === "species" && (
          <div className="container-dh py-10">
            <div className="flex flex-col md:flex-row gap-4 mb-8">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate/50" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search species..."
                  className="w-full h-12 pl-11 pr-4 bg-bone border border-carbon/20 font-body text-sm text-carbon placeholder:text-slate/50 focus:outline-none focus:border-brass transition-colors"
                  data-testid="species-search-input"
                />
              </div>
              <div className="flex gap-2 overflow-x-auto hide-scrollbar">
                <button onClick={() => setSelectedCategory(null)} className={`px-4 py-2 font-label text-xs font-semibold tracking-wider uppercase whitespace-nowrap transition-colors ${!selectedCategory ? "bg-brass text-carbon" : "border border-carbon/20 text-slate hover:text-carbon"}`}>All</button>
                {CATEGORY_ORDER.map((cat) => (
                  <button key={cat} onClick={() => setSelectedCategory(cat)} className={`px-4 py-2 font-label text-xs font-semibold tracking-wider uppercase whitespace-nowrap transition-colors ${selectedCategory === cat ? "bg-brass text-carbon" : "border border-carbon/20 text-slate hover:text-carbon"}`}>{cat}</button>
                ))}
              </div>
            </div>
            <p className="font-body text-sm text-slate mb-6">{filteredSpecies.length} species found</p>
            {groupByCategory(filteredSpecies).map(({ category, species }) => (
              <div key={category} className="mb-10">
                <h3 className="font-display text-xl font-bold text-carbon mb-4 pb-2 border-b border-carbon/15">{category}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {species.map((s) => (
                    <Link key={s.slug} to={`/expeditions/${s.slug}`} className="block border border-carbon/10 bg-chalk/30 p-5 hover:border-brass/30 hover:shadow-lg transition-all" data-testid={`species-browse-${s.slug}`}>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-display text-lg font-bold text-carbon">{s.name}</h4>
                        <span className="font-label text-[9px] tracking-wider uppercase text-slate/60">{s.difficulty}</span>
                      </div>
                      <p className="font-body text-xs text-slate mb-3 line-clamp-2">{s.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-sm text-carbon">${s.price_low.toLocaleString()} – ${s.price_high.toLocaleString()}</span>
                        <span className="font-body text-xs text-brass">{s.season}</span>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {s.countries.slice(0, 5).map((c) => (
                          <span key={c} className="font-label text-[9px] tracking-wider uppercase bg-carbon/5 text-slate px-2 py-0.5">{c}</span>
                        ))}
                        {s.countries.length > 5 && <span className="font-label text-[9px] text-slate">+{s.countries.length - 5}</span>}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ─── SEASON TAB ─── */}
        {activeTab === "season" && (
          <div className="container-dh py-10">
            <div className="flex gap-2 overflow-x-auto hide-scrollbar mb-8">
              <button onClick={() => setSelectedMonth(null)} className={`px-4 py-3 font-label text-xs font-semibold tracking-wider uppercase whitespace-nowrap transition-colors ${!selectedMonth ? "bg-brass text-carbon" : "border border-carbon/20 text-slate hover:text-carbon"}`}>All Months</button>
              {MONTHS.map((m) => (
                <button key={m} onClick={() => setSelectedMonth(m)} className={`px-4 py-3 font-label text-xs font-semibold tracking-wider uppercase whitespace-nowrap transition-colors ${selectedMonth === m ? "bg-brass text-carbon" : "border border-carbon/20 text-slate hover:text-carbon"}`}>{m}</button>
              ))}
            </div>
            <p className="font-body text-sm text-slate mb-6">
              {selectedMonth ? `${seasonSpecies.length} species available in ${selectedMonth}` : `${allSpecies.length} species — select a month to filter`}
            </p>
            {groupByCategory(seasonSpecies).map(({ category, species }) => (
              <div key={category} className="mb-10">
                <h3 className="font-display text-xl font-bold text-carbon mb-4 pb-2 border-b border-carbon/15">{category}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {species.map((s) => (
                    <Link key={s.slug} to={`/expeditions/${s.slug}`} className="block border border-carbon/10 bg-chalk/30 p-5 hover:border-brass/30 hover:shadow-lg transition-all" data-testid={`season-species-${s.slug}`}>
                      <h4 className="font-display text-lg font-bold text-carbon mb-1">{s.name}</h4>
                      <p className="font-label text-[10px] tracking-wider uppercase text-brass mb-2">{s.category}</p>
                      <p className="font-body text-xs text-slate mb-3">{s.season}</p>
                      <span className="font-mono text-sm text-carbon">${s.price_low.toLocaleString()} – ${s.price_high.toLocaleString()}</span>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ─── BUDGET TAB ─── */}
        {activeTab === "budget" && (
          <div className="container-dh py-10">
            <div className="flex gap-2 overflow-x-auto hide-scrollbar mb-8">
              <button onClick={() => setSelectedBudget(null)} className={`px-4 py-3 font-label text-xs font-semibold tracking-wider uppercase whitespace-nowrap transition-colors ${!selectedBudget ? "bg-brass text-carbon" : "border border-carbon/20 text-slate hover:text-carbon"}`}>All Budgets</button>
              {BUDGET_RANGES.map((b) => (
                <button key={b.label} onClick={() => setSelectedBudget(b)} className={`px-4 py-3 font-label text-xs font-semibold tracking-wider uppercase whitespace-nowrap transition-colors ${selectedBudget?.label === b.label ? "bg-brass text-carbon" : "border border-carbon/20 text-slate hover:text-carbon"}`}>{b.label}</button>
              ))}
            </div>
            <p className="font-body text-sm text-slate mb-6">
              {selectedBudget ? `${budgetSpecies.length} species in the ${selectedBudget.label} range` : `${allSpecies.length} species — select a budget range`}
            </p>
            {groupByCategory(budgetSpecies).map(({ category, species }) => (
              <div key={category} className="mb-10">
                <h3 className="font-display text-xl font-bold text-carbon mb-4 pb-2 border-b border-carbon/15">{category}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {species.map((s) => (
                    <Link key={s.slug} to={`/expeditions/${s.slug}`} className="block border border-carbon/10 bg-chalk/30 p-5 hover:border-brass/30 hover:shadow-lg transition-all" data-testid={`budget-species-${s.slug}`}>
                      <h4 className="font-display text-lg font-bold text-carbon mb-1">{s.name}</h4>
                      <p className="font-label text-[10px] tracking-wider uppercase text-brass mb-2">{s.category} &middot; {s.difficulty}</p>
                      <p className="font-body text-xs text-slate mb-3 line-clamp-2">{s.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-sm font-bold text-carbon">${s.price_low.toLocaleString()} – ${s.price_high.toLocaleString()}</span>
                        <span className="font-body text-xs text-slate">{s.season}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Bottom CTA */}
      <section className="bg-carbon py-16">
        <div className="container-dh text-center">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-bone mb-4">Ready to start planning?</h2>
          <p className="font-body text-base text-bone/60 mb-8 max-w-lg mx-auto">Tell us what you want to hunt and we'll build your trip from the ground up.</p>
          <Link to="/apply" className="btn-brass" data-testid="browse-bottom-cta">Find Your Hunt <ArrowRight className="w-4 h-4 ml-2" /></Link>
        </div>
      </section>
    </>
  );
}
